import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
	useRouteMatch,
	useLocation,
	Route,
	Switch,
	Redirect,
	Link,
} from "react-router-dom";
import { useWeb3React } from "@web3-react/core";

import Mint from "./Mint";

import { Button, Card, CardBody, Spinner } from "reactstrap";
import Box from "@mui/material/Box";
import AssetsNavigation from "../components/assets/AssetsNavigation";
import AssetsContractInfo from "../components/assets/AssetsContractInfo";
import AssetsToken from "../components/assets/AssetsToken";

import BreadCrumbs from "../components/Breadcrumbs";
import NeedAuth from "../components/NeedAuth";
import Avatar from "../components/Avatar";

import uploadAPI from "../api/uploadAPI";
import { chainIds } from "../constants";
import { useTokenStatus } from "../hooks/useTokenStatus";
import CopyClipboard from "../components/common/CopyClipboard";
import { ethers } from "ethers";
import bn from "bignumber.js";
import { ethersToBN, getContractAbi } from "../utils";

function AssetsPage({ account }) {
	const { url } = useRouteMatch();
	const { pathname } = useLocation();

	const { getTokensTotal } = useTokenStatus();
	const { chainId, library } = useWeb3React();

	const {
		contract: { items, templates },
		user: { user },
	} = useSelector((state) => state);

	const contracts = useSelector((state) => state.contract.items);

	const [isTokensAmount, setIsTokensAmount] = useState(true);
	const [tokensAmount, setTokensAmount] = useState(null);
	const [erc20TokensAmount, setErc20TokensAmount] = useState("");
	const [erc20TokensAmountOwned, setErc20TokensAmountOwned] = useState("");

	const [isUnlocksAmount, setIsUnlocksAmount] = useState(true);
	const [unlocksAmount, setUnlocksAmount] = useState(0);
	const [contractUseddGas, setContractUseddGas] = useState(0);
	const [tokentUsedGas, setTokenUsedGas] = useState(0);

	const getERC20Stats = useCallback(async () => {
		const supplyPromises = [];
		const balancePromises = [];

		for (const contractTemplate of contracts) {
			if (contractTemplate.type === "ERC20") {
				const contract = new ethers.Contract(
					contractTemplate.address,
					getContractAbi(contractTemplate.type, contractTemplate.name),
					!!library ? library.getSigner() : library
				);

				supplyPromises.push(contract.totalSupply());

				if (account) {
					balancePromises.push(contract.balanceOf(account));
				}
			}
		}

		const decimals = new bn(10).pow(18);

		const supplies = await Promise.all(supplyPromises);
		const suppliesSum = supplies.reduce(
			(acc, next) => acc.add(next),
			ethers.BigNumber.from("0")
		);

		setErc20TokensAmount(
			ethersToBN(suppliesSum).div(decimals).toNumber().toFixed(0)
		);

		const balances = await Promise.all(balancePromises);
		const balancesSum = balances.reduce(
			(acc, next) => acc.add(next),
			ethers.BigNumber.from("0")
		);

		setErc20TokensAmountOwned(
			ethersToBN(balancesSum).div(decimals).toNumber().toFixed()
		);
	}, [account, contracts, library]);

	useEffect(() => {
		getERC20Stats();
	}, [getERC20Stats]);

	useEffect(() => {
		setIsTokensAmount(false);
		setTokensAmount(null);
		setTokenUsedGas(0);
		getTokensTotal().then((res) => {
			setTokensAmount(res);
			setIsTokensAmount(true);
			setTokenUsedGas(res.totalGasMint?.toFixed(8));
		});

		return () => {};
	}, [items, getTokensTotal]);

	useEffect(() => {
		if (items.length > 0) {
			setIsUnlocksAmount(false);
			setUnlocksAmount(0);
			setContractUseddGas(0);
			if (items.length > 0) {
				let gas = 0;
				items.forEach(async (contract, i) => {
					gas += contract.gas;
					const { data: amount } = await uploadAPI.get(
						`unlock/contract/${contract.address}`
					);
					setUnlocksAmount((prevValue) => prevValue + amount);

					if (i === items.length - 1) {
						setIsUnlocksAmount(true);
					}
				});
				setContractUseddGas(gas.toFixed(8));
			} else {
				setIsTokensAmount(true);
				setIsUnlocksAmount(true);
			}
		}
	}, [items]);

	const renderUserImg = () => {
		if (user !== null && user.image) {
			return (
				<img
					height="110"
					width="110"
					alt="user-avatar"
					src={`${process.env.REACT_APP_API_DO_URL}/${user.image}`}
					className="img-fluid rounded mt-3 mb-2"
				/>
			);
		} else {
			const stateNum = Math.floor(Math.random() * 6),
				states = [
					"light-success",
					"light-danger",
					"light-warning",
					"light-info",
					"light-primary",
					"light-secondary",
				],
				color = states[stateNum];
			return (
				<Avatar
					initials
					color={color}
					className="rounded mt-3 mb-2"
					content={user.name || "Anonymous"}
					contentStyles={{
						borderRadius: 6,
						fontSize: "calc(48px)",
						width: "100%",
						height: "100%",
					}}
					style={{
						height: "110px",
						width: "110px",
					}}
				/>
			);
		}
	};

	const AddDataButton = () => (
		<Link to="/user">
			<Button color="primary">Add data</Button>
		</Link>
	);

	return (
		<Box
			sx={{ minHeight: "78vh", margin: "0 auto", width: "100%" }}
			maxWidth="lg"
		>
			<BreadCrumbs breadCrumbTitle="Contracts" crumbs={pathname.split("/")} />
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					flexWrap: "nowrap",
					minHeight: "60vh",
				}}
			>
				{!pathname.split("/").includes("mint") && (
					<AssetsNavigation pathname={pathname} />
				)}
				<Switch>
					<Route path={`${url}`} exact>
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								flexBasis: "75%",
							}}
						>
							<Box
								sx={{
									minHeight: "78vh",
									margin: "0 auto",
									width: "100%",
								}}
							>
								<Card className="mb-3">
									<CardBody
										style={{
											paddingLeft: "70px",
											paddingRight: "30px",
										}}
									>
										<div className="d-flex">
											<div
												className="user-avatar-section me-5"
												style={{
													minHeight: "110px",
													minWidth: "110px",
												}}
											>
												<div className="d-flex align-items-center flex-column">
													{renderUserImg()}
												</div>
											</div>
											<div className="info-container" style={{ width: "100%" }}>
												<h4 className="fw-bolder border-bottom pb-50 mb-1">
													Your contracts details
												</h4>
												<table className="mt-2 mt-xl-0 w-100">
													<tbody>
														<tr className="table-row">
															<th className="pe-3">Name</th>
															<td>{user.name || <AddDataButton />}</td>
														</tr>
														<tr className="table-row">
															<th className="pe-3">Email</th>
															<td>{user.email || <AddDataButton />}</td>
														</tr>
														<tr className="table-row">
															<th className="pe-3">Role</th>
															<td>
																{user.isAdmin ? "Administrator" : "Customer"}
															</td>
														</tr>
														<tr className="table-row">
															<th className="pe-3">Conected to</th>
															<td>{chainIds[chainId].name}</td>
														</tr>
														<tr className="table-row">
															<th className="pe-3">Wallet Address</th>
															<td>
																{user.account}
																<CopyClipboard target={user.account} />
															</td>
														</tr>
														<tr className="table-row">
															<th className="pe-3">Wallet Link</th>
															<td>
																<a
																	href={`${chainIds[chainId].link}${user.account}`}
																	rel="noreferrer"
																	target="_blank"
																>{`Link to wallet Etherscan`}</a>
															</td>
														</tr>
														<tr className="table-row">
															<th className="pe-3">URL</th>
															<td>{user.customURL || <AddDataButton />}</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>
										<div>
											<table className="mt-2 mt-xl-0 w-100">
												<tbody>
													<tr className="table-row">
														<th>Allowed Master Contracts</th>
														<td>
															{templates.filter((i) => i.isAllowed).length}
														</td>
													</tr>
													<tr className="table-row">
														<th>Total Contracts</th>
														<td>{items.length}</td>
													</tr>
													<tr className="table-row">
														<th>Used GAS for deployed</th>
														<td>{contractUseddGas ?? <Spinner size="sm" />}</td>
													</tr>
													<tr className="table-row">
														<th>Used GAS for minting</th>
														<td>{tokentUsedGas ?? <Spinner size="sm" />}</td>
													</tr>
													<tr className="table-row">
														<th></th>
														<th>Total</th>
														<th>Owned</th>
														<th>Transferred</th>
														<th>Burnt</th>
													</tr>
													<tr className="table-row">
														<th>Tokens ERC721</th>
														<td>
															{isTokensAmount ? (
																tokensAmount?.totalSingle ?? 0
															) : (
																<Spinner size="sm" />
															)}
														</td>
														<td>
															{isTokensAmount ? (
																tokensAmount?.ownedSingle ?? 0
															) : (
																<Spinner size="sm" />
															)}
														</td>
														<td>
															{isTokensAmount ? (
																tokensAmount?.transferredSingle ?? 0
															) : (
																<Spinner size="sm" />
															)}
														</td>
														<td>
															{isTokensAmount ? (
																tokensAmount?.burntSingle ?? 0
															) : (
																<Spinner size="sm" />
															)}
														</td>
													</tr>
													<tr className="table-row">
														<th>Tokens ERC1155</th>
														<td>
															{isTokensAmount ? (
																`${tokensAmount?.totalMulti ?? 0}(${
																	tokensAmount?.totalMultiAmount ?? 0
																})`
															) : (
																<Spinner size="sm" />
															)}
														</td>
														<td>
															{isTokensAmount ? (
																`${tokensAmount?.ownedMulti ?? 0}(${
																	tokensAmount?.ownedMultiAmount ?? 0
																})`
															) : (
																<Spinner size="sm" />
															)}
														</td>
														<td>
															{isTokensAmount ? (
																`${tokensAmount?.transferredMultiAmount ?? 0}`
															) : (
																<Spinner size="sm" />
															)}
														</td>
														<td>
															{isTokensAmount ? (
																tokensAmount?.burntMulti ?? 0
															) : (
																<Spinner size="sm" />
															)}
														</td>
													</tr>
													<tr className="table-row">
														<th>Tokens ERC20</th>
														<td>
															{erc20TokensAmount ? (
																erc20TokensAmount
															) : (
																<Spinner size="sm" />
															)}
														</td>
														<td>
															{erc20TokensAmountOwned ? (
																erc20TokensAmountOwned
															) : (
																<Spinner size="sm" />
															)}
														</td>
													</tr>
													<tr className="table-row">
														<th>Lockable Content</th>
														<td>
															{isUnlocksAmount ? (
																unlocksAmount
															) : (
																<Spinner size="sm" />
															)}
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</CardBody>
								</Card>
							</Box>
						</Box>
					</Route>
					<Route path={`${url}/last`} exact>
						{items.length && <Redirect to={`${url}/${items.at(-1)._id}`} />}
					</Route>
					<Route
						path={`${url}/token/:tokenId`}
						render={(props) => (
							<AssetsToken tokenId={props.match.params.tokenId} />
						)}
					/>
					<Route path={`${url}/mint`}>
						{account ? <Mint /> : <NeedAuth />}
					</Route>
					<Route
						path={`${url}/:contractId`}
						render={(props) => (
							<AssetsContractInfo contractId={props.match.params.contractId} />
						)}
					/>
				</Switch>
			</Box>
		</Box>
	);
}

export default AssetsPage;
